import { directorScriptOf } from "./scriptStory";

type EnvelopeInput = Record<string, unknown> | undefined;

export type VoiceSelectionSummary = {
  voiceType: "predefined" | "generated" | "none" | "unknown";
  title: string;
  voiceId: string | null;
  providerLabel: string;
  sourceLabel: string;
  rationale: string;
  requirements: string[];
  profile: string[];
  decisionPathLabel: string | null;
  candidateCount: number | null;
  catalogReleaseId: string | null;
  catalogDigest: string | null;
  renderProfileId: string | null;
  renderProfileDigest: string | null;
  seed: number | null;
  legacy: boolean;
};

const SOURCE_LABELS: Record<string, string> = {
  interview_director: "Interview + director brief",
  skill_policy: "Autonomous skill policy",
  occasion_policy: "Occasion policy",
};

const PATH_LABELS: Record<string, string> = {
  skipped_no_voice: "No voice requested",
  skipped_musical: "Musical occasion",
  reference_exact: "Approved reference match",
  single_candidate: "Only valid catalog voice",
  llm_shortlist: "Director shortlist choice",
  generated_no_valid_candidate: "No valid catalog voice",
  generated_invalid_selection: "Selector fallback",
  policy_fallback_no_valid_candidate: "Signed policy fallback",
  policy_fallback_invalid_selection: "Signed selector fallback",
};

export function summarizeVoiceSelection(
  envelope: EnvelopeInput,
  isNarrated?: boolean | null,
): VoiceSelectionSummary | null {
  const audit = voiceAuditOf(envelope);
  if (!audit) return legacyVoiceSummary(envelope, isNarrated);

  const voice = recordValue(audit.voice);
  const decision = recordValue(audit.decision);
  const requirements = recordValue(audit.requirements);
  const profile = recordValue(voice?.profile);
  const resolvedVoice = recordValue(audit.resolved_voice);
  const voiceType = voiceTypeValue(voice?.type);
  const voiceId = stringValue(voice?.voice_id);
  const name = stringValue(voice?.name);
  const source = stringValue(decision?.source);
  const sourceDetail = stringValue(decision?.source_detail);
  const path = stringValue(decision?.path);

  return {
    voiceType,
    title: voiceTitle(voiceType, name, voiceId),
    voiceId,
    providerLabel: providerLabel(audit.provider),
    sourceLabel: sourceLabel(source, sourceDetail),
    rationale:
      stringValue(decision?.rationale) ??
      "The stored audit record does not include a rationale.",
    requirements: requirementLabels(requirements),
    profile: profileLabels(profile),
    decisionPathLabel: path ? (PATH_LABELS[path] ?? humanize(path)) : null,
    candidateCount: numberValue(decision?.candidate_count),
    catalogReleaseId: stringValue(
      audit.catalog_release_id ?? resolvedVoice?.catalog_release_id,
    ),
    catalogDigest: stringValue(
      audit.catalog_digest ?? resolvedVoice?.catalog_digest,
    ),
    renderProfileId: stringValue(
      audit.render_profile_id ?? resolvedVoice?.render_profile_id,
    ),
    renderProfileDigest: stringValue(resolvedVoice?.render_profile_digest),
    seed: numberValue(resolvedVoice?.seed),
    legacy: false,
  };
}

export type VoiceLanguageSummary = {
  /** Language the narration was authored in (Echo projection, else envelope). */
  contentLanguage: string | null;
  /** `voiceover.language_code_sent` from the composer's TTS diagnostics. */
  languageCodeSent: string | null;
};

/**
 * Narration language evidence for the voice card (plan 2026-09-21 §4.9,
 * LANG-300/303/501). The Echo projection's `content_language` wins; the
 * script envelope's own `content_language` (declared on GenScript by
 * LANG-300) is the fallback for backends that do not project it. The TTS
 * language code the composer actually sent rides in
 * `diagnostics.voiceover.language_code_sent` when the model accepts one.
 */
export function summarizeVoiceLanguage(
  envelope: EnvelopeInput,
  contentLanguage?: string | null,
): VoiceLanguageSummary {
  const diagnostics = recordValue(envelope?.diagnostics);
  const voiceover = recordValue(diagnostics?.voiceover);
  return {
    contentLanguage:
      stringValue(contentLanguage) ?? stringValue(envelope?.content_language),
    languageCodeSent: stringValue(voiceover?.language_code_sent),
  };
}

function voiceAuditOf(envelope: EnvelopeInput): Record<string, unknown> | null {
  const diagnostics = recordValue(envelope?.diagnostics);
  const audit = recordValue(diagnostics?.voice_selection);
  return ["voice_selection.v1", "voice_selection.v2"].includes(
    String(audit?.schema_version ?? ""),
  )
    ? audit
    : null;
}

function legacyVoiceSummary(
  envelope: EnvelopeInput,
  isNarrated?: boolean | null,
): VoiceSelectionSummary | null {
  const voice = directorScriptOf(envelope)?.voice_generation;
  const genType = stringValue(voice?.gen_type);
  const voiceId = stringValue(voice?.voice_id);
  if (!genType && !isNarrated) return null;

  const voiceType = voiceTypeValue(genType);
  return {
    voiceType,
    title: voiceTitle(voiceType, null, voiceId),
    voiceId,
    providerLabel: "ElevenLabs",
    sourceLabel: "Legacy script",
    rationale:
      "This historical Echo predates persisted voice-selection rationale, so the original reason is unavailable.",
    requirements: [],
    profile: [],
    decisionPathLabel: null,
    candidateCount: null,
    catalogReleaseId: null,
    catalogDigest: null,
    renderProfileId: null,
    renderProfileDigest: null,
    seed: null,
    legacy: true,
  };
}

function requirementLabels(
  requirements: Record<string, unknown> | null,
): string[] {
  if (!requirements) return [];
  const hard = recordValue(requirements.hard);
  const labels = [
    traitLabel("Gender", hard?.gender),
    traitLabel("Age", hard?.age),
    traitLabel("Accent", hard?.accent),
  ].filter((label): label is string => Boolean(label));
  const ageDescription = stringValue(requirements.age_description);
  if (ageDescription) labels.push(`Age preference: ${ageDescription}`);
  const preferences = stringList(requirements.director_preferences);
  labels.push(
    ...preferences.map(
      (preference) => `Director preference: ${humanize(preference)}`,
    ),
  );
  if (requirements.approved_reference_profile === true) {
    labels.push("Approved reference profile");
  }
  return labels;
}

function profileLabels(profile: Record<string, unknown> | null): string[] {
  if (!profile) return [];
  return [
    traitLabel("Gender", profile.gender),
    traitLabel("Age", profile.age),
    traitLabel("Accent", profile.accent),
  ].filter((label): label is string => Boolean(label));
}

function traitLabel(label: string, value: unknown): string | null {
  const text = stringValue(value);
  return text ? `${label}: ${humanize(text)}` : null;
}

function voiceTitle(
  voiceType: VoiceSelectionSummary["voiceType"],
  name: string | null,
  voiceId: string | null,
): string {
  if (voiceType === "none") return "No narration voice";
  if (voiceType === "generated") return "Custom generated voice";
  if (name) return name;
  if (voiceId) return `Predefined voice ${voiceId}`;
  return "Narrated voice";
}

function sourceLabel(source: string | null, detail: string | null): string {
  if (source === "skill_policy" && detail) {
    return `${humanize(detail)} voice policy`;
  }
  return source
    ? (SOURCE_LABELS[source] ?? humanize(source))
    : "Director decision";
}

function providerLabel(value: unknown): string {
  const provider = stringValue(value);
  return provider?.toLowerCase() === "elevenlabs"
    ? "ElevenLabs"
    : provider
      ? humanize(provider)
      : "ElevenLabs";
}

function voiceTypeValue(value: unknown): VoiceSelectionSummary["voiceType"] {
  if (value === "predefined" || value === "generated" || value === "none") {
    return value;
  }
  if (value === "generate") return "generated";
  return "unknown";
}

function humanize(value: string): string {
  const spaced = value.replaceAll("_", " ").trim();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : value;
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function numberValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}
